import {
  animate,
  animation,
  AnimationTriggerMetadata,
  group,
  keyframes,
  style,
  transition,
  trigger,
  useAnimation
} from '@angular/animations';

// ---------------------------------------------------------------------------
// Interfaces
// ---------------------------------------------------------------------------

export interface IAnimationOptions {
  anchor?: string;
  duration?: number;
  delay?: number;
}

export interface ISlideAnimationOptions extends IAnimationOptions {
  translate?: string;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function timings(duration: number, delay: number): string {
  return `${duration}ms ${delay}ms`;
}

// ---------------------------------------------------------------------------
// Core animation definitions (reusable)
// ---------------------------------------------------------------------------

const fadeInRef = () =>
  animation([
    animate(
      '{{duration}}ms {{delay}}ms',
      keyframes([
        style({ visibility: 'visible', opacity: 0, easing: 'ease', offset: 0 }),
        style({ opacity: 1, easing: 'ease', offset: 1 })
      ])
    )
  ]);

const fadeOutRef = () =>
  animation([
    animate(
      '{{duration}}ms {{delay}}ms',
      keyframes([
        style({ opacity: 1, easing: 'ease', offset: 0 }),
        style({ opacity: 0, easing: 'ease', offset: 1 })
      ])
    )
  ]);

const fadeInDownRef = () =>
  animation([
    animate(
      '{{duration}}ms {{delay}}ms',
      keyframes([
        style({ visibility: 'visible', opacity: 0, transform: 'translate3d(0, -{{translate}}, 0)', easing: 'ease', offset: 0 }),
        style({ opacity: 1, transform: 'translate3d(0, 0, 0)', easing: 'ease', offset: 1 })
      ])
    )
  ]);

const fadeOutDownRef = () =>
  animation([
    animate(
      '{{duration}}ms {{delay}}ms',
      keyframes([
        style({ opacity: 1, transform: 'translate3d(0, 0, 0)', easing: 'ease', offset: 0 }),
        style({ opacity: 0, transform: 'translate3d(0, {{translate}}, 0)', easing: 'ease', offset: 1 })
      ])
    )
  ]);

const slideInUpRef = () =>
  animation([
    animate(
      '{{duration}}ms {{delay}}ms',
      keyframes([
        style({ visibility: 'visible', transform: 'translate3d(0, {{translate}}, 0)', easing: 'ease', offset: 0 }),
        style({ transform: 'translate3d(0, 0, 0)', easing: 'ease', offset: 1 })
      ])
    )
  ]);

const slideOutDownRef = () =>
  animation([
    animate(
      '{{duration}}ms {{delay}}ms',
      keyframes([
        style({ transform: 'translate3d(0, 0, 0)', easing: 'ease', offset: 0 }),
        style({ transform: 'translate3d(0, {{translate}}, 0)', visibility: 'hidden', easing: 'ease', offset: 1 })
      ])
    )
  ]);

const slideInDownRef = () =>
  animation([
    animate(
      '{{duration}}ms {{delay}}ms',
      keyframes([
        style({ visibility: 'visible', transform: 'translate3d(0, -{{translate}}, 0)', easing: 'ease', offset: 0 }),
        style({ transform: 'translate3d(0, 0, 0)', easing: 'ease', offset: 1 })
      ])
    )
  ]);

const slideOutUpRef = () =>
  animation([
    animate(
      '{{duration}}ms {{delay}}ms',
      keyframes([
        style({ transform: 'translate3d(0, 0, 0)', easing: 'ease', offset: 0 }),
        style({ transform: 'translate3d(0, -{{translate}}, 0)', visibility: 'hidden', easing: 'ease', offset: 1 })
      ])
    )
  ]);

const slideInLeftRef = () =>
  animation([
    animate(
      '{{duration}}ms {{delay}}ms',
      keyframes([
        style({ visibility: 'visible', transform: 'translate3d(-{{translate}}, 0, 0)', easing: 'ease', offset: 0 }),
        style({ transform: 'translate3d(0, 0, 0)', easing: 'ease', offset: 1 })
      ])
    )
  ]);

const slideOutLeftRef = () =>
  animation([
    animate(
      '{{duration}}ms {{delay}}ms',
      keyframes([
        style({ transform: 'translate3d(0, 0, 0)', easing: 'ease', offset: 0 }),
        style({ transform: 'translate3d(-{{translate}}, 0, 0)', visibility: 'hidden', easing: 'ease', offset: 1 })
      ])
    )
  ]);

const slideInRightRef = () =>
  animation([
    animate(
      '{{duration}}ms {{delay}}ms',
      keyframes([
        style({ visibility: 'visible', transform: 'translate3d({{translate}}, 0, 0)', easing: 'ease', offset: 0 }),
        style({ transform: 'translate3d(0, 0, 0)', easing: 'ease', offset: 1 })
      ])
    )
  ]);

const slideOutRightRef = () =>
  animation([
    animate(
      '{{duration}}ms {{delay}}ms',
      keyframes([
        style({ transform: 'translate3d(0, 0, 0)', easing: 'ease', offset: 0 }),
        style({ transform: 'translate3d({{translate}}, 0, 0)', visibility: 'hidden', easing: 'ease', offset: 1 })
      ])
    )
  ]);

const zoomInRef = () =>
  animation(
    group([
      animate(
        '{{duration}}ms {{delay}}ms',
        keyframes([
          style({ opacity: 0, easing: 'ease', offset: 0 }),
          style({ opacity: 1, easing: 'ease', offset: 0.5 }),
          style({ opacity: 1, easing: 'ease', offset: 1 })
        ])
      ),
      animate(
        '{{duration}}ms {{delay}}ms',
        keyframes([
          style({ visibility: 'visible', transform: 'scale3d(0.3, 0.3, 0.3)', easing: 'ease', offset: 0 }),
          style({ transform: 'scale3d(1, 1, 1)', easing: 'ease', offset: 1 })
        ])
      )
    ])
  );

const zoomOutRef = () =>
  animation(
    group([
      animate(
        '{{duration}}ms {{delay}}ms',
        keyframes([
          style({ opacity: 1, transform: 'scale3d(1, 1, 1)', easing: 'ease', offset: 0 }),
          style({ opacity: 0, transform: 'scale3d(0.3, 0.3, 0.3)', easing: 'ease', offset: 0.5 }),
          style({ opacity: 0, easing: 'ease', offset: 1 })
        ])
      ),
      animate(
        '{{duration}}ms {{delay}}ms',
        keyframes([
          style({ transform: 'scale3d(1, 1, 1)', easing: 'ease', offset: 0 }),
          style({ transform: 'scale3d(0.3, 0.3, 0.3)', easing: 'ease', offset: 0.5 })
        ])
      )
    ])
  );

// ---------------------------------------------------------------------------
// Default durations (match library defaults)
// ---------------------------------------------------------------------------

const DEFAULT_DURATION = 1000;
const DEFAULT_TRANSLATE = '100%';

// ---------------------------------------------------------------------------
// Fade enter/leave
// ---------------------------------------------------------------------------

export function fadeInOnEnterAnimation(options?: IAnimationOptions): AnimationTriggerMetadata {
  const duration = options?.duration ?? DEFAULT_DURATION;
  const delay = options?.delay ?? 0;
  return trigger(options?.anchor ?? 'fadeInOnEnter', [
    transition(':enter', [
      style({ visibility: 'hidden' }),
      useAnimation(fadeInRef(), { params: { duration, delay } })
    ], { params: { duration, delay } })
  ]);
}

export function fadeOutOnLeaveAnimation(options?: IAnimationOptions): AnimationTriggerMetadata {
  const duration = options?.duration ?? DEFAULT_DURATION;
  const delay = options?.delay ?? 0;
  return trigger(options?.anchor ?? 'fadeOutOnLeave', [
    transition(':leave', [
      useAnimation(fadeOutRef(), { params: { duration, delay } })
    ], { params: { duration, delay } })
  ]);
}

// ---------------------------------------------------------------------------
// Fade-down enter/leave
// ---------------------------------------------------------------------------

export function fadeInDownOnEnterAnimation(options?: ISlideAnimationOptions): AnimationTriggerMetadata {
  const duration = options?.duration ?? DEFAULT_DURATION;
  const delay = options?.delay ?? 0;
  const translate = options?.translate ?? DEFAULT_TRANSLATE;
  return trigger(options?.anchor ?? 'fadeInDownOnEnter', [
    transition(':enter', [
      style({ visibility: 'hidden' }),
      useAnimation(fadeInDownRef(), { params: { duration, delay, translate } })
    ], { params: { duration, delay, translate } })
  ]);
}

export function fadeOutDownOnLeaveAnimation(options?: ISlideAnimationOptions): AnimationTriggerMetadata {
  const duration = options?.duration ?? DEFAULT_DURATION;
  const delay = options?.delay ?? 0;
  const translate = options?.translate ?? DEFAULT_TRANSLATE;
  return trigger(options?.anchor ?? 'fadeOutDownOnLeave', [
    transition(':leave', [
      useAnimation(fadeOutDownRef(), { params: { duration, delay, translate } })
    ], { params: { duration, delay, translate } })
  ]);
}

// ---------------------------------------------------------------------------
// Slide up / down enter/leave
// ---------------------------------------------------------------------------

export function slideInUpOnEnterAnimation(options?: ISlideAnimationOptions): AnimationTriggerMetadata {
  const duration = options?.duration ?? DEFAULT_DURATION;
  const delay = options?.delay ?? 0;
  const translate = options?.translate ?? DEFAULT_TRANSLATE;
  return trigger(options?.anchor ?? 'slideInUpOnEnter', [
    transition(':enter', [
      style({ visibility: 'hidden' }),
      useAnimation(slideInUpRef(), { params: { duration, delay, translate } })
    ], { params: { duration, delay, translate } })
  ]);
}

export function slideOutDownOnLeaveAnimation(options?: ISlideAnimationOptions): AnimationTriggerMetadata {
  const duration = options?.duration ?? DEFAULT_DURATION;
  const delay = options?.delay ?? 0;
  const translate = options?.translate ?? DEFAULT_TRANSLATE;
  return trigger(options?.anchor ?? 'slideOutDownOnLeave', [
    transition(':leave', [
      useAnimation(slideOutDownRef(), { params: { duration, delay, translate } })
    ], { params: { duration, delay, translate } })
  ]);
}

export function slideInDownOnEnterAnimation(options?: ISlideAnimationOptions): AnimationTriggerMetadata {
  const duration = options?.duration ?? DEFAULT_DURATION;
  const delay = options?.delay ?? 0;
  const translate = options?.translate ?? DEFAULT_TRANSLATE;
  return trigger(options?.anchor ?? 'slideInDownOnEnter', [
    transition(':enter', [
      style({ visibility: 'hidden' }),
      useAnimation(slideInDownRef(), { params: { duration, delay, translate } })
    ], { params: { duration, delay, translate } })
  ]);
}

export function slideOutUpOnLeaveAnimation(options?: ISlideAnimationOptions): AnimationTriggerMetadata {
  const duration = options?.duration ?? DEFAULT_DURATION;
  const delay = options?.delay ?? 0;
  const translate = options?.translate ?? DEFAULT_TRANSLATE;
  return trigger(options?.anchor ?? 'slideOutUpOnLeave', [
    transition(':leave', [
      useAnimation(slideOutUpRef(), { params: { duration, delay, translate } })
    ], { params: { duration, delay, translate } })
  ]);
}

// ---------------------------------------------------------------------------
// Slide left / right enter/leave
// ---------------------------------------------------------------------------

export function slideInLeftOnEnterAnimation(options?: ISlideAnimationOptions): AnimationTriggerMetadata {
  const duration = options?.duration ?? DEFAULT_DURATION;
  const delay = options?.delay ?? 0;
  const translate = options?.translate ?? DEFAULT_TRANSLATE;
  return trigger(options?.anchor ?? 'slideInLeftOnEnter', [
    transition(':enter', [
      style({ visibility: 'hidden' }),
      useAnimation(slideInLeftRef(), { params: { duration, delay, translate } })
    ], { params: { duration, delay, translate } })
  ]);
}

export function slideOutLeftOnLeaveAnimation(options?: ISlideAnimationOptions): AnimationTriggerMetadata {
  const duration = options?.duration ?? DEFAULT_DURATION;
  const delay = options?.delay ?? 0;
  const translate = options?.translate ?? DEFAULT_TRANSLATE;
  return trigger(options?.anchor ?? 'slideOutLeftOnLeave', [
    transition(':leave', [
      useAnimation(slideOutLeftRef(), { params: { duration, delay, translate } })
    ], { params: { duration, delay, translate } })
  ]);
}

export function slideInRightOnEnterAnimation(options?: ISlideAnimationOptions): AnimationTriggerMetadata {
  const duration = options?.duration ?? DEFAULT_DURATION;
  const delay = options?.delay ?? 0;
  const translate = options?.translate ?? DEFAULT_TRANSLATE;
  return trigger(options?.anchor ?? 'slideInRightOnEnter', [
    transition(':enter', [
      style({ visibility: 'hidden' }),
      useAnimation(slideInRightRef(), { params: { duration, delay, translate } })
    ], { params: { duration, delay, translate } })
  ]);
}

export function slideOutRightOnLeaveAnimation(options?: ISlideAnimationOptions): AnimationTriggerMetadata {
  const duration = options?.duration ?? DEFAULT_DURATION;
  const delay = options?.delay ?? 0;
  const translate = options?.translate ?? DEFAULT_TRANSLATE;
  return trigger(options?.anchor ?? 'slideOutRightOnLeave', [
    transition(':leave', [
      useAnimation(slideOutRightRef(), { params: { duration, delay, translate } })
    ], { params: { duration, delay, translate } })
  ]);
}

// ---------------------------------------------------------------------------
// Zoom enter/leave
// ---------------------------------------------------------------------------

export function zoomInOnEnterAnimation(options?: IAnimationOptions): AnimationTriggerMetadata {
  const duration = options?.duration ?? DEFAULT_DURATION;
  const delay = options?.delay ?? 0;
  return trigger(options?.anchor ?? 'zoomInOnEnter', [
    transition(':enter', [
      style({ visibility: 'hidden' }),
      useAnimation(zoomInRef(), { params: { duration, delay } })
    ], { params: { duration, delay } })
  ]);
}

export function zoomOutOnLeaveAnimation(options?: IAnimationOptions): AnimationTriggerMetadata {
  const duration = options?.duration ?? DEFAULT_DURATION;
  const delay = options?.delay ?? 0;
  return trigger(options?.anchor ?? 'zoomOutOnLeave', [
    transition(':leave', [
      useAnimation(zoomOutRef(), { params: { duration, delay } })
    ], { params: { duration, delay } })
  ]);
}

// ---------------------------------------------------------------------------
// Modal-specific animations (state-based for close animation support)
// ---------------------------------------------------------------------------

export function modalBodyAnimation(): AnimationTriggerMetadata {
  return trigger('modalBody', [
    transition('void => open', [
      style({ opacity: 0, transform: 'scale(0.96) translateY(10px)' }),
      animate('280ms cubic-bezier(0.16, 1, 0.3, 1)',
        style({ opacity: 1, transform: 'scale(1) translateY(0)' }))
    ]),
    transition('open => closing', [
      animate('200ms cubic-bezier(0.4, 0, 1, 1)',
        style({ opacity: 0, transform: 'scale(0.96) translateY(10px)' }))
    ])
  ]);
}

export function modalBackdropAnimation(): AnimationTriggerMetadata {
  return trigger('modalBackdrop', [
    transition('void => open', [
      style({ opacity: 0 }),
      animate('280ms cubic-bezier(0.16, 1, 0.3, 1)',
        style({ opacity: 1 }))
    ]),
    transition('open => closing', [
      animate('200ms cubic-bezier(0.4, 0, 1, 1)',
        style({ opacity: 0 }))
    ])
  ]);
}
